import React from 'react';
import { logout } from '../utils/session';
import { PUBLIC_URL } from '../utils/const';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LoadingOverlay from './LoadingOverlay';
import { Burger, Group, Container, Box, Anchor, Stack, Spoiler } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useOptionalAuth } from '../contexts/AuthContext';
import { useLocalizedPath } from '../utils/localeNav';

const NavBar: React.FC<{ verify_session?: boolean, onlyRequired?: boolean, dashboard?: boolean }> = ({ verify_session = true, onlyRequired = false, dashboard = false }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [opened, { toggle }] = useDisclosure();
  const spoilerControlRef = useRef<HTMLButtonElement>(null);
  const auth = useOptionalAuth();
  const href = useLocalizedPath();
  const isAuthenticated = verify_session ? auth?.isAuthenticated ?? false : false;
  const user = auth?.user ?? null;
  const get_isAuthenticated = () => isAuthenticated === true;

  useEffect(() => {
    if (spoilerControlRef.current) {
      spoilerControlRef.current.click();
    }
  }, [opened]);

  function handleLogout() {
    setIsLoading(true);
    logout().then(() => {
      window.location.href = href('/dashboard')
    }).finally(() => setIsLoading(false))
  }

  const minimalAuthHeader = dashboard && verify_session && get_isAuthenticated();

  return (
    <Box
      className={minimalAuthHeader ? 'hero hero-navbar' : 'hero py-2 hero-navbar'}
      py={minimalAuthHeader ? { base: 'xs', md: 'xs' } : { base: 'md', md: 'lg' }}
    >
      {isLoading && <LoadingOverlay />}
      <Container size="lg" py={minimalAuthHeader ? 0 : 'xs'}>
        <Group h="100%" justify="space-between">
          <Group>
            <Anchor href={href('/')} p={0} h='30'>
              <img
                src={`${PUBLIC_URL}/logo-small.png`}
                width={30}
                height={30}
                alt="Calensync logo"
              />
            </Anchor>
            {verify_session && get_isAuthenticated() && !minimalAuthHeader ? (
              <>
                <Group visibleFrom="sm">
                  <a href={href('/dashboard?tab=plan')} className="mantine-link">{t('navbar.plan')}</a>
                  <a href={href('/dashboard?tab=team')} className="mantine-link">{t('navbar.team')}</a>
                  {user?.is_admin && (
                    <a href={href('/admin')} className="mantine-link">Admin</a>
                  )}
                  <a href="#" onClick={handleLogout} className="mantine-link">{t('navbar.logout')}</a>
                </Group>
              </>
            ) : !minimalAuthHeader ? (
              <Group visibleFrom="sm">
                <a href={href('/')} className="mantine-link">{t('navbar.home')}</a>
                <a href={href('/dashboard')} className="mantine-link">{t('navbar.dashboard')}</a>
                <a href={href('/mcp')} className="mantine-link">{t('navbar.mcp')}</a>
                <a href={href('/blog')} className="mantine-link">{t('navbar.blog')}</a>
                <a href={href('/#pricing')} className="mantine-link">{t('navbar.pricing')}</a>
              </Group>
            ) : null}
          </Group>

          <>
            {verify_session && get_isAuthenticated() && minimalAuthHeader &&
              <Anchor visibleFrom="sm" c="gray.7" fs="sm" href="#" onClick={(e) => { e.preventDefault(); handleLogout(); }}>
                {t('navbar.logout')}
              </Anchor>
            }
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
          </>
        </Group>
        <Spoiler hideLabel='' showLabel='' maxHeight={0} controlRef={spoilerControlRef} my={0} hiddenFrom="sm">
          {verify_session && get_isAuthenticated() ? (
            <>
              <Stack py={minimalAuthHeader ? 'sm' : 'lg'}>
                {minimalAuthHeader ? (
                  <>
                    {user?.is_admin && (
                      <a href={href('/admin')} className="mantine-link">Admin</a>
                    )}
                    <a href="#" onClick={handleLogout} className="mantine-link">{t('navbar.logout')}</a>
                  </>
                ) : (
                  <>
                    <a href={href('/')} className="mantine-link">{t('navbar.home')}</a>
                    <a href={href('/dashboard?tab=plan')} className="mantine-link">{t('navbar.plan')}</a>
                    <a href={href('/dashboard?tab=team')} className="mantine-link">{t('navbar.team')}</a>
                    {user?.is_admin && (
                      <a href={href('/admin')} className="mantine-link">Admin</a>
                    )}
                    <a href="#" onClick={handleLogout} className="mantine-link">{t('navbar.logout')}</a>
                    <a href="https://tally.so/r/nroQgv" target="_blank" className="mantine-link">
                      💬 Feature Request / 🐞 Issue
                    </a>
                  </>
                )}
              </Stack>
            </>
          ) : (
            <Stack py={'lg'}>
              <a href={href('/')} className="mantine-link">{t('navbar.home')}</a>
              <a href={href('/dashboard')} className="mantine-link">{t('navbar.dashboard')}</a>
              <a href={href('/mcp')} className="mantine-link">{t('navbar.mcp')}</a>
              <a href={href('/blog')} className="mantine-link">{t('navbar.blog')}</a>
              <a href={href('/#pricing')} className="mantine-link">{t('navbar.pricing')}</a>
              <a href={href('/#features')} className="mantine-link">{t('navbar.features')}</a>
            </Stack>
          )}
        </Spoiler>
      </Container>
    </Box>
  );
}

export default NavBar;